import { useEffect, useRef } from "react";

/**
 * Drives a CSS custom property from a live volume ref using RAF.
 * Useful for UI audio-reactive effects without re-rendering React on each frame.
 */
export default function useAudioPulseCssVar({
  targetRef,
  volumeRef,
  enabled,
  cssVar = "--mr-ai-pulse",
  gate = 0.015,
  min = 0,
  max = 1,
  precision = 3,
}) {
  const rafRef = useRef(null);

  useEffect(() => {
    const el = targetRef?.current;
    if (!el) return undefined;

    if (!enabled) {
      el.style.setProperty(cssVar, "0");
      return undefined;
    }

    const tick = () => {
      const raw = Number(volumeRef?.current || 0);
      const gated = raw < gate ? 0 : raw;
      const next = Math.min(max, Math.max(min, gated));
      el.style.setProperty(cssVar, next.toFixed(precision));
      rafRef.current = requestAnimationFrame(tick);
    };

    tick();

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      el.style.setProperty(cssVar, "0");
    };
  }, [cssVar, enabled, gate, max, min, precision, targetRef, volumeRef]);
}

